import { Component, OnInit } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import Swal from 'sweetalert2';
import { AuthService } from 'src/app/authentication/auth.service';
import { PerfilesAccesoService } from 'src/app/services/empresa/perfiles-acceso.service';
import { UsuariosService } from 'src/app/services/personas/usuarios.service';

interface ProfileItem {
  id: number;
  name: string;
}

interface PermissionItem {
  id: number;
  code: string;
  name: string;
  description: string;
}

interface ScreenAccess {
  key: string;
  label: string;
  description: string;
  permissions: string[];
  children?: ScreenAccess[];
}

interface UsuarioPerfilRow {
  id: number;
  nombre: string;
  usuario: string;
  perfil: string;
  profile_id: number | null;
  estado: string;
}

// Acceso base oculto: todos los perfiles tendrán al menos consulta de órdenes.
const BASELINE_PERMISSION_CODES: string[] = [
  'transacciones.ordenes.read_list',
  'transacciones.ordenes.read_item',
  'empresa.sucursales.read',
  'empresa.paises.read',
  'empresa.especialidades.read'
];

const HIDDEN_PERMISSION_CODES: string[] = [
  'auditoria.subidas.read'
];

const SUBIR_RESULTADOS_CHILDREN: ScreenAccess[] = [
  {
    key: 'subir-resultados-ecografias',
    label: 'Ecografías',
    description: 'Acceso al módulo de carga/gestión de ecografías.',
    permissions: ['subir_resultados.ecografias']
  },
  {
    key: 'subir-resultados-rx-periapical',
    label: 'RX Periapical',
    description: 'Acceso al módulo de carga/gestión de RX Periapical.',
    permissions: ['subir_resultados.rx_periapical']
  },
  {
    key: 'subir-resultados-rx-panoramica',
    label: 'RX Panorámica',
    description: 'Acceso al módulo de carga/gestión de RX Panorámica.',
    permissions: ['subir_resultados.rx_panoramica']
  },
  {
    key: 'subir-resultados-tomografias',
    label: 'Tomografías',
    description: 'Acceso al módulo de carga/gestión de tomografías.',
    permissions: ['subir_resultados.tomografias']
  },
  {
    key: 'subir-resultados-analisis-cefa',
    label: 'Análisis Cefalométrico',
    description: 'Acceso al módulo de análisis cefalométrico.',
    permissions: ['subir_resultados.analisis_cefalometrico']
  },
  {
    key: 'subir-resultados-tomografia-zip',
    label: 'Tomografía .zip',
    description: 'Acceso al módulo de carga de tomografías en .zip.',
    permissions: ['subir_resultados.tomografia_zip']
  },
  {
    key: 'subir-resultados-rx-corporal-imagenes',
    label: 'RX Corporal (Imágenes)',
    description: 'Acceso al módulo RX Corporal de imágenes.',
    permissions: ['subir_resultados.rx_corporal_imagenes']
  },
  {
    key: 'subir-resultados-rx-corporal-informes',
    label: 'RX Corporal (Informes)',
    description: 'Acceso al módulo RX Corporal de informes.',
    permissions: ['subir_resultados.rx_corporal_informes']
  },
  {
    key: 'subir-resultados-informes-rx',
    label: 'Informes RX',
    description: 'Acceso al módulo de informes RX.',
    permissions: ['subir_resultados.informes_rx']
  }
];

const CREAR_ORDEN_CORE_PERMISSIONS: string[] = [
  'transacciones.ordenes.create',
  'transacciones.ordenes.update',
  'transacciones.ordenes.patch',
  'personas.pacientes.read',
  'personas.pacientes.create',
  'personas.pacientes.update',
  'personas.pacientes.patch',
  'personas.doctores.read',
  'personas.doctores.create',
  'personas.doctores.update',
  'personas.doctores.patch',
  'inventario.servicios.read',
  'empresa.sucursales.read',
  'empresa.paises.read',
  'empresa.especialidades.read'
];

const SCREEN_ACCESS: ScreenAccess[] = [
  {
    key: 'flujo-crear-orden',
    label: 'Gestión de órdenes',
    description: 'Crear/editar orden, buscar y gestionar paciente/doctor y consultar servicios.',
    permissions: [
      'transacciones.ordenes.create',
      'transacciones.ordenes.update',
      'transacciones.ordenes.patch',
      'transacciones.ordenes.delete',
      'personas.pacientes.read',
      'personas.pacientes.create',
      'personas.pacientes.update',
      'personas.pacientes.patch',
      'personas.doctores.read',
      'personas.doctores.create',
      'personas.doctores.update',
      'personas.doctores.patch',
      'inventario.servicios.read',
      'empresa.sucursales.read',
      'transacciones.ordenes.tab.pendientes',
      'transacciones.ordenes.tab.pendientes_facturadas',
      'transacciones.ordenes.tab.atendidas',
      'transacciones.ordenes.tab.anuladas',
      'transacciones.ordenes.tab.paquetes',
      'transacciones.ordenes.tab.tomografia_dicom',
      'transacciones.ordenes.tab.tomografia',
      'transacciones.ordenes.nueva.electronica_factura',
      'transacciones.ordenes.nueva.electronica_convenio',
      'transacciones.ordenes.nueva.fisica_factura',
      'transacciones.ordenes.nueva.fisica_sin_factura',
      'transacciones.ordenes.nueva.electronica_cita_previa'
    ],
    children: [
      {
        key: 'inicio-tab-pendientes',
        label: 'Pestaña: Pendientes',
        description: 'Permite ver la pestaña Pendientes.',
        permissions: ['transacciones.ordenes.tab.pendientes']
      },
      {
        key: 'inicio-tab-pendientes-facturadas',
        label: 'Pestaña: Pendientes Facturadas',
        description: 'Permite ver la pestaña Pendientes Facturadas.',
        permissions: ['transacciones.ordenes.tab.pendientes_facturadas']
      },
      {
        key: 'inicio-tab-atendidas',
        label: 'Pestaña: Atendidas',
        description: 'Permite ver la pestaña Atendidas.',
        permissions: ['transacciones.ordenes.tab.atendidas']
      },
      {
        key: 'inicio-tab-anuladas',
        label: 'Pestaña: Anuladas',
        description: 'Permite ver la pestaña Anuladas.',
        permissions: ['transacciones.ordenes.tab.anuladas']
      },
      {
        key: 'inicio-tab-paquetes',
        label: 'Pestaña: Paquetes',
        description: 'Permite ver la pestaña Paquetes.',
        permissions: ['transacciones.ordenes.tab.paquetes']
      },
      {
        key: 'inicio-tab-tomografia-dicom',
        label: 'Pestaña: Tomografía Dicom',
        description: 'Permite ver la pestaña Tomografía Dicom.',
        permissions: ['transacciones.ordenes.tab.tomografia_dicom']
      },
      {
        key: 'inicio-tab-tomografia',
        label: 'Pestaña: Tomografía',
        description: 'Permite ver la pestaña Tomografía.',
        permissions: ['transacciones.ordenes.tab.tomografia']
      },
      {
        key: 'inicio-nueva-electronica-factura',
        label: 'Nueva: Orden electrónica con Factura',
        description: 'Permite crear orden electrónica con factura.',
        permissions: [
          'transacciones.ordenes.nueva.electronica_factura',
          ...CREAR_ORDEN_CORE_PERMISSIONS
        ]
      },
      {
        key: 'inicio-nueva-electronica-convenio',
        label: 'Nueva: Orden electrónica (Convenio)',
        description: 'Permite crear orden electrónica por convenio.',
        permissions: [
          'transacciones.ordenes.nueva.electronica_convenio',
          ...CREAR_ORDEN_CORE_PERMISSIONS
        ]
      },
      {
        key: 'inicio-nueva-fisica-factura',
        label: 'Nueva: Orden Física con Factura',
        description: 'Permite crear orden física con factura.',
        permissions: [
          'transacciones.ordenes.nueva.fisica_factura',
          ...CREAR_ORDEN_CORE_PERMISSIONS
        ]
      },
      {
        key: 'inicio-nueva-fisica-sin-factura',
        label: 'Nueva: Orden Física sin Factura',
        description: 'Permite crear orden física sin factura.',
        permissions: [
          'transacciones.ordenes.nueva.fisica_sin_factura',
          ...CREAR_ORDEN_CORE_PERMISSIONS
        ]
      },
      {
        key: 'inicio-nueva-cita-previa',
        label: 'Nueva: Orden Electrónica (Cita Previa)',
        description: 'Permite crear orden electrónica de cita previa.',
        permissions: [
          'transacciones.ordenes.nueva.electronica_cita_previa',
          ...CREAR_ORDEN_CORE_PERMISSIONS
        ]
      }
    ]
  },
  {
    key: 'reportes',
    label: 'Reportes',
    description: 'Control de acceso a reportes.',
    permissions: [
      'transacciones.reportes.ventas',
      'transacciones.reportes.envio'
    ],
    children: [
      {
        key: 'reportes-ventas',
        label: 'Reporte Ventas',
        description: 'Permite visualizar la opción Ventas dentro del menú Reportes.',
        permissions: ['transacciones.reportes.ventas']
      },
      {
        key: 'reportes-envio',
        label: 'Envío Reporte',
        description: 'Permite visualizar la opción Envío Reporte dentro del menú Reportes.',
        permissions: ['transacciones.reportes.envio']
      }
    ]
  },
  {
    key: 'gestion-tomografias',
    label: 'Gestión Tomografías',
    description: 'Gestionar requerimientos y flujo operativo de tomografías.',
    permissions: [
      'transacciones.requerimientos.read',
      'transacciones.requerimientos.create',
      'transacciones.requerimientos.update',
      'transacciones.requerimientos.patch',
      'transacciones.requerimientos.delete'
    ]
  },
  {
    key: 'ordenes-fisicas',
    label: 'Órdenes físicas',
    description: 'Solicitudes, seguimiento, asignaciones, tipos de precio y precios.',
    permissions: [
      'empresa.solicitudes_ordenes_fisicas.read',
      'empresa.solicitudes_ordenes_fisicas.inicializar.read',
      'empresa.solicitudes_ordenes_fisicas.siguiente_secuencia.read',
      'empresa.solicitudes_ordenes_fisicas.disponibles.read',
      'empresa.solicitudes_ordenes_fisicas.blocks.read',
      'empresa.solicitudes_ordenes_fisicas.secuencias.read',
      'empresa.solicitudes_ordenes_fisicas.asesores.read',
      'empresa.solicitudes_ordenes_fisicas.doctores.read',
      'empresa.solicitudes_ordenes_fisicas.prefijos.create',
      'empresa.solicitudes_ordenes_fisicas.generar_blocks.create',
      'empresa.solicitudes_ordenes_fisicas.create',
      'empresa.solicitudes_ordenes_fisicas.asignar_asesor.update',
      'empresa.solicitudes_ordenes_fisicas.asignar_doctor.update',
      'empresa.solicitudes_ordenes_fisicas.asignar_doctor_secuencia.update',
      'empresa.tipo_precios.read',
      'empresa.tipo_precios.create',
      'empresa.tipo_precios.update',
      'empresa.tipo_precios.delete',
      'empresa.precios_ordenes_fisicas.read',
      'empresa.precios_ordenes_fisicas.create',
      'empresa.precios_ordenes_fisicas.update',
      'empresa.precios_ordenes_fisicas.delete'
    ]
  },
  {
    key: 'laboratorio',
    label: 'Laboratorio',
    description: 'Gestión de resultados, proveedores derivados e información de pruebas.',
    permissions: [
      'laboratorio.resultados.read',
      'laboratorio.laboratorios.read',
      'laboratorio.laboratorios.create',
      'laboratorio.laboratorios.update',
      'laboratorio.proveedores_derivados.read',
      'laboratorio.proveedores_derivados.create',
      'laboratorio.proveedores_derivados.update',
      'laboratorio.proveedores_derivados.delete',
      'laboratorio.informacion_pruebas.read',
      'laboratorio.informacion_pruebas.create',
      'laboratorio.informacion_pruebas.update',
      'laboratorio.informacion_pruebas.delete',
      'laboratorio.scanear_muestras.read',
      'laboratorio.estados_muestras.read'
    ],
    children: [
      {
        key: 'laboratorio-resultados',
        label: 'Resultados',
        description: 'Ver y gestionar resultados de órdenes de laboratorio por sucursal.',
        permissions: [
          'laboratorio.resultados.read'
        ]
      },
      {
        key: 'laboratorio-scanear-muestras',
        label: 'Scanear Muestras',
        description: 'Permite escanear códigos de muestras y actualizar su estado desde móvil.',
        permissions: [
          'laboratorio.scanear_muestras.read'
        ]
      },
      {
        key: 'laboratorio-estados-muestras',
        label: 'Estados de muestras',
        description: 'Permite consultar y gestionar el estado de las muestras de laboratorio.',
        permissions: [
          'laboratorio.estados_muestras.read'
        ]
      },
      {
        key: 'laboratorio-proveedores-derivados',
        label: 'Proveedores (Derivados)',
        description: 'Permite gestionar laboratorios y su oferta comercial.',
        permissions: [
          'laboratorio.laboratorios.read',
          'laboratorio.laboratorios.create',
          'laboratorio.laboratorios.update',
          'laboratorio.proveedores_derivados.read',
          'laboratorio.proveedores_derivados.create',
          'laboratorio.proveedores_derivados.update',
          'laboratorio.proveedores_derivados.delete'
        ]
      },
      {
        key: 'laboratorio-informacion-pruebas',
        label: 'Información pruebas',
        description: 'Permite gestionar pruebas, muestras, contenedor y condiciones.',
        permissions: [
          'laboratorio.informacion_pruebas.read',
          'laboratorio.informacion_pruebas.create',
          'laboratorio.informacion_pruebas.update',
          'laboratorio.informacion_pruebas.delete'
        ]
      }
    ]
  },
  {
    key: 'gestion-clinica',
    label: 'Gestión Clínica',
    description: 'Gestión de historias clínicas, historial de pacientes, evoluciones y edición clínica.',
    permissions: [
      'gestion_clinica.historia_clinica.read',
      'gestion_clinica.historia_clinica.create',
      'gestion_clinica.historia_clinica.update',
      'gestion_clinica.historial_pacientes.read',
      'gestion_clinica.evoluciones.create',
      'gestion_clinica.auditoria.read'
    ],
    children: [
      {
        key: 'gestion-clinica-historia',
        label: 'Historia Clínica',
        description: 'Ver órdenes pendientes y crear la historia clínica inicial.',
        permissions: [
          'gestion_clinica.historia_clinica.read',
          'gestion_clinica.historia_clinica.create'
        ]
      },
      {
        key: 'gestion-clinica-historial',
        label: 'Historial de Pacientes',
        description: 'Consultar historias clínicas registradas, PDF, órdenes relacionadas y evoluciones.',
        permissions: [
          'gestion_clinica.historial_pacientes.read'
        ]
      },
      {
        key: 'gestion-clinica-editar',
        label: 'Editar Historia Clínica',
        description: 'Permite modificar una historia clínica registrada.',
        permissions: [
          'gestion_clinica.historia_clinica.update'
        ]
      },
      {
        key: 'gestion-clinica-evoluciones',
        label: 'Agregar Evolución',
        description: 'Permite registrar evoluciones posteriores a la historia inicial.',
        permissions: [
          'gestion_clinica.evoluciones.create'
        ]
      },
      {
        key: 'gestion-clinica-auditoria',
        label: 'Auditoría Clínica',
        description: 'Permite consultar quién creó, editó o agregó evoluciones, con IP y dispositivo.',
        permissions: [
          'gestion_clinica.auditoria.read'
        ]
      }
    ]
  },
  {
    key: 'subir-resultados',
    label: 'Subir Resultados',
    description: 'Asigna acceso total o parcial por tipo de resultado.',
    permissions: SUBIR_RESULTADOS_CHILDREN.flatMap((item) => item.permissions),
    children: SUBIR_RESULTADOS_CHILDREN
  },
  {
    key: 'seguridad',
    label: 'Seguridad',
    description: 'Administración de perfiles/permisos y gestión de usuarios.',
    permissions: [
      'empresa.perfiles_acceso.profiles.read',
      'empresa.perfiles_acceso.profiles.create',
      'empresa.perfiles_acceso.profiles.update',
      'empresa.perfiles_acceso.profiles.delete',
      'empresa.perfiles_acceso.permissions.read',
      'empresa.perfiles_acceso.profile.read',
      'empresa.perfiles_acceso.profile.update',
      'admin.users.read',
      'admin.users.update'
    ],
    children: [
      {
        key: 'admin-rbac',
        label: 'Perfiles y permisos',
        description: 'Gestionar perfiles y su matriz de permisos.',
        permissions: [
          'empresa.perfiles_acceso.profiles.read',
          'empresa.perfiles_acceso.profiles.create',
          'empresa.perfiles_acceso.profiles.update',
          'empresa.perfiles_acceso.profiles.delete',
          'empresa.perfiles_acceso.permissions.read',
          'empresa.perfiles_acceso.profile.read',
          'empresa.perfiles_acceso.profile.update'
        ]
      },
      {
        key: 'admin-usuarios',
        label: 'Usuarios',
        description: 'Consultar y administrar usuarios del sistema.',
        permissions: [
          'admin.users.read',
          'admin.users.update'
        ]
      }
    ]
  },
  {
    key: 'administracion-solicitudes-precios',
    label: 'Administración / Solicitud precios',
    description: 'Gestionar aprobación o rechazo de solicitudes de cambio de precio.',
    permissions: [
      'empresa.solicitudes_precios_administracion.read',
      'empresa.solicitudes_precios_administracion.update'
    ]
  },
  {
    key: 'configuraciones',
    label: 'Configuraciones',
    description: 'Configuración de sellos, firmas digitales y datos de firmantes.',
    permissions: [
      'empresa.configuracion.sellos_firmas.read',
      'empresa.configuracion.sellos_firmas.write'
    ],
    children: [
      {
        key: 'configuraciones-sellos-firmas',
        label: 'Sellos y Firmas',
        description: 'Ver y editar sellos y firmas digitales por usuario.',
        permissions: [
          'empresa.configuracion.sellos_firmas.read',
          'empresa.configuracion.sellos_firmas.write'
        ]
      }
    ]
  },
  {
    key: 'seguridad-acceso-ip',
    label: 'Accesos IP',
    description: 'Gestionar IPs autorizadas por sucursal.',
    permissions: ['empresa.seguridad.acceso_ip.manage']
  },
  {
    key: 'transacciones',
    label: 'Transacciones',
    description: 'Módulos de transacciones del sistema (facturas, notas de crédito, envío masivo y descuentos).',
    permissions: [
      'transacciones.documentos_electronicos.read_list',
      'transacciones.ordenes_convenio.read_list',
      'transacciones.facturas.create',
      'transacciones.notas_credito.read_list',
      'transacciones.envio_masivo.read_list',
      'transacciones.descuentos.read_list',
      'transacciones.descuentos.create',
      'transacciones.descuentos.delete',
      'personas.pacientes.read',
      'personas.pacientes.create',
      'personas.doctores.read',
      'inventario.servicios.read',
      'empresa.especialidades.read',
      'empresa.paises.read'
    ],
    children: [
      {
        key: 'transacciones-documentos-electronicos',
        label: 'Facturas Electrónicas',
        description: 'Ver el listado de facturas electrónicas, generar el RIDE y exportar a Excel.',
        permissions: [
          'transacciones.documentos_electronicos.read_list'
        ]
      },
      {
        key: 'transacciones-ordenes-convenio',
        label: 'Ordenes Convenio',
        description: 'Ver órdenes por convenio pendientes de factura y enviarlas al flujo de facturación.',
        permissions: [
          'transacciones.ordenes_convenio.read_list',
          'transacciones.ordenes.read_item'
        ]
      },
      {
        key: 'transacciones-facturas',
        label: 'Facturación de Órdenes',
        description: 'Crear la factura electrónica de una orden desde el flujo de creación/facturación.',
        permissions: [
          'transacciones.facturas.create'
        ]
      },
      {
        key: 'transacciones-notas-credito',
        label: 'Notas de Crédito',
        description: 'Ver el listado de notas de crédito y exportarlas.',
        permissions: [
          'transacciones.notas_credito.read_list'
        ]
      },
      {
        key: 'transacciones-envio-masivo',
        label: 'Envío Masivo SRI',
        description: 'Consultar comprobantes pendientes por fecha y enviarlos masivamente al SRI.',
        permissions: [
          'transacciones.envio_masivo.read_list'
        ]
      },
      {
        key: 'transacciones-descuentos',
        label: 'Descuentos',
        description: 'Ver listado, crear y eliminar descuentos sin pasar por SRI. Incluye acceso a pacientes, doctores y servicios.',
        permissions: [
          'transacciones.descuentos.read_list',
          'transacciones.descuentos.create',
          'transacciones.descuentos.delete',
          'personas.pacientes.read',
          'personas.pacientes.create',
          'personas.doctores.read',
          'inventario.servicios.read',
          'empresa.especialidades.read',
          'empresa.paises.read'
        ]
      }
    ]
  }
];

@Component({
    selector: 'app-perfiles-acceso',
    templateUrl: './perfiles-acceso.component.html',
    styleUrls: ['./perfiles-acceso.component.scss'],
    standalone: false
})
export class PerfilesAccesoComponent implements OnInit {

  public cargandoPerfiles = false;
  public cargandoPermisos = false;
  public guardando = false;
  public collapsedSections: Set<string> = new Set<string>(
    SCREEN_ACCESS.filter(s => s.children && s.children.length > 0).map(s => s.key)
  );

  public profiles: ProfileItem[] = [];
  public selectedProfile: ProfileItem | null = null;

  public permissions: PermissionItem[] = [];
  public selectedPermissionCodes: Set<string> = new Set<string>();
  public busquedaPerfil = '';
  public busquedaPantalla = '';
  public paginaPerfilesActual = 1;
  public tamanoPaginaPerfiles = 5;

  private availablePermissionCodes = new Set<string>();
  private usuariosCache: UsuarioPerfilRow[] | null = null;

  constructor(
    private perfilesAccesoService: PerfilesAccesoService,
    private usuariosService: UsuariosService,
    private toastr: ToastrService,
    public authService: AuthService
  ) {}

  ngOnInit(): void {
    this.cargarDatosIniciales();
  }

  get token(): string {
    return localStorage.getItem('token') || '';
  }

  get pantallas(): ScreenAccess[] {
    return SCREEN_ACCESS;
  }

  get perfilesFiltrados(): ProfileItem[] {
    const filtro = this.busquedaPerfil.trim().toLowerCase();
    if (!filtro) {
      return this.profiles;
    }

    return this.profiles.filter((profile) => {
      const nombre = String(profile.name || '').toLowerCase();
      const id = String(profile.id || '');
      return nombre.includes(filtro) || id.includes(filtro);
    });
  }

  get totalPaginasPerfiles(): number {
    const total = this.perfilesFiltrados.length;
    return Math.max(1, Math.ceil(total / this.tamanoPaginaPerfiles));
  }

  get perfilesPaginados(): ProfileItem[] {
    const totalPaginas = this.totalPaginasPerfiles;
    const pagina = Math.min(Math.max(this.paginaPerfilesActual, 1), totalPaginas);
    const inicio = (pagina - 1) * this.tamanoPaginaPerfiles;
    const fin = inicio + this.tamanoPaginaPerfiles;
    return this.perfilesFiltrados.slice(inicio, fin);
  }

  get pantallasFiltradas(): ScreenAccess[] {
    const filtro = this.busquedaPantalla.trim().toLowerCase();
    if (!filtro) {
      return this.pantallas;
    }

    return this.pantallas
      .map((item) => {
        const itemMatch = this.matchesScreen(item, filtro);
        if (!item.children || !item.children.length) {
          return itemMatch ? item : null;
        }

        if (itemMatch) {
          return item;
        }

        const childrenFiltered = item.children.filter((child) => this.matchesScreen(child, filtro));
        if (!childrenFiltered.length) {
          return null;
        }

        return {
          ...item,
          children: childrenFiltered
        };
      })
      .filter((item): item is ScreenAccess => !!item);
  }

  get totalPantallasSeleccionadas(): number {
    return this.flattenPantallas(this.pantallas).filter((item) => this.isPantallaChecked(item)).length;
  }

  get canReadProfiles(): boolean {
    return this.authService.hasPermission('empresa.perfiles_acceso.profiles.read');
  }

  get canCreateProfiles(): boolean {
    return this.authService.hasPermission('empresa.perfiles_acceso.profiles.create');
  }

  get canUpdateProfiles(): boolean {
    return this.authService.hasPermission('empresa.perfiles_acceso.profiles.update');
  }

  get canDeleteProfiles(): boolean {
    return this.authService.hasPermission('empresa.perfiles_acceso.profiles.delete');
  }

  get canReadPermissions(): boolean {
    return this.authService.hasPermission('empresa.perfiles_acceso.permissions.read');
  }

  get canReadUsers(): boolean {
    return this.authService.hasPermission('admin.users.read');
  }

  get canReadProfilePermissions(): boolean {
    return this.authService.hasPermission('empresa.perfiles_acceso.profile.read');
  }

  get canUpdateProfilePermissions(): boolean {
    return this.authService.hasPermission('empresa.perfiles_acceso.profile.update');
  }

  async cargarDatosIniciales() {
    if (!this.canReadProfiles) {
      this.showError('No tienes permiso para consultar perfiles.');
      return;
    }

    this.cargandoPerfiles = true;
    this.cargandoPermisos = true;

    this.perfilesAccesoService.listarPerfiles(this.token).subscribe((resp: any) => {
      this.profiles = this.toArray(resp).map((item: any) => ({
        id: Number(item.id),
        name: String(item.name || '')
      }));
      this.ajustarPaginaPerfiles();

      this.cargandoPerfiles = false;

      if (this.profiles.length > 0 && !this.selectedProfile) {
        this.seleccionarPerfil(this.profiles[0]);
      }
    });

    if (!this.canReadPermissions) {
      this.permissions = [];
      this.availablePermissionCodes = new Set<string>();
      this.cargandoPermisos = false;
      return;
    }

    this.perfilesAccesoService.listarPermisos(this.token).subscribe((resp: any) => {
      this.permissions = this.toArray(resp)
        .map((item: any) => ({
          id: Number(item.id),
          code: String(item.code || ''),
          name: String(item.name || ''),
          description: String(item.description || '')
        }))
        .filter((item: PermissionItem) => !HIDDEN_PERMISSION_CODES.includes(item.code));

      this.availablePermissionCodes = new Set<string>(this.permissions.map((item) => item.code));
      this.cargandoPermisos = false;
    });
  }

  seleccionarPerfil(profile: ProfileItem) {
    this.selectedProfile = profile;

    if (!this.canReadProfilePermissions) {
      this.selectedPermissionCodes = new Set<string>();
      return;
    }

    this.cargandoPermisos = true;
    this.perfilesAccesoService.listarPermisosPorPerfil(this.token, profile.id).subscribe((resp: any) => {
      const rows = this.toArray(resp);
      this.selectedPermissionCodes = new Set<string>(
        rows
          .map((item: any) => String(item.code || ''))
          .filter((code: string) => !HIDDEN_PERMISSION_CODES.includes(code))
      );
      this.cargandoPermisos = false;
    });
  }

  getPantallaCodes(pantalla: ScreenAccess): string[] {
    const own = pantalla.permissions || [];
    const children = (pantalla.children || []).flatMap((child) => child.permissions || []);
    const all = [...own, ...children];
    return all.filter((code, index) => all.indexOf(code) === index).filter((code) => this.availablePermissionCodes.has(code));
  }

  private getEffectiveCodes(): Set<string> {
    return new Set<string>([...Array.from(this.selectedPermissionCodes), ...BASELINE_PERMISSION_CODES]);
  }

  isPantallaChecked(pantalla: ScreenAccess): boolean {
    const codes = this.getPantallaCodes(pantalla);
    if (!codes.length) {
      return false;
    }

    const effective = this.getEffectiveCodes();
    return codes.every((code) => effective.has(code));
  }

  isPantallaPartial(pantalla: ScreenAccess): boolean {
    const codes = this.getPantallaCodes(pantalla);
    if (!codes.length) {
      return false;
    }

    const effective = this.getEffectiveCodes();
    const selected = codes.filter((code) => effective.has(code)).length;
    return selected > 0 && selected < codes.length;
  }

  pantallaDisponible(pantalla: ScreenAccess): boolean {
    return this.getPantallaCodes(pantalla).length > 0;
  }

  togglePantalla(pantalla: ScreenAccess, event: Event) {
    const target = event.target as HTMLInputElement;
    if (!target) {
      return;
    }

    const codes = this.getPantallaCodes(pantalla);

    if (target.checked) {
      for (const code of codes) {
        this.selectedPermissionCodes.add(code);
      }
    } else {
      for (const code of codes) {
        this.selectedPermissionCodes.delete(code);
      }
    }

    this.selectedPermissionCodes = new Set<string>(this.selectedPermissionCodes);
  }

  seleccionarTodo() {
    for (const pantalla of this.flattenPantallas(this.pantallas)) {
      for (const code of this.getPantallaCodes(pantalla)) {
        this.selectedPermissionCodes.add(code);
      }
    }
    this.selectedPermissionCodes = new Set<string>(this.selectedPermissionCodes);
  }

  limpiarTodo() {
    for (const pantalla of this.flattenPantallas(this.pantallas)) {
      for (const code of this.getPantallaCodes(pantalla)) {
        this.selectedPermissionCodes.delete(code);
      }
    }
    this.selectedPermissionCodes = new Set<string>(this.selectedPermissionCodes);
  }

  guardarPermisosPerfil() {
    if (!this.canUpdateProfilePermissions) {
      this.showError('No tienes permiso para actualizar permisos de perfil.');
      return;
    }

    if (!this.selectedProfile) {
      this.showError('Selecciona un perfil primero.');
      return;
    }

    this.guardando = true;
    const permissions = Array.from(new Set<string>([
      ...Array.from(this.selectedPermissionCodes),
      ...BASELINE_PERMISSION_CODES
    ])).filter((code) => !HIDDEN_PERMISSION_CODES.includes(code));

    this.perfilesAccesoService
      .actualizarPermisosPerfil(this.token, this.selectedProfile.id, permissions)
      .subscribe({
        next: () => {
          this.guardando = false;
          this.toastr.success('Permisos actualizados correctamente.', 'Atención', {
            timeOut: 3000,
            closeButton: true,
            progressBar: true
          });
        },
        error: () => {
          this.guardando = false;
          this.showError('No se pudieron actualizar los permisos.');
        }
      });
  }

  async crearPerfil() {
    if (!this.canCreateProfiles) {
      this.showError('No tienes permiso para crear perfiles.');
      return;
    }

    const result = await Swal.fire({
      title: 'Nuevo perfil',
      input: 'text',
      inputPlaceholder: 'Ejemplo: Supervisor',
      showCancelButton: true,
      confirmButtonText: 'Crear',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#6259ca',
      inputValidator: (value) => {
        if (!String(value || '').trim()) {
          return 'Debes ingresar un nombre de perfil';
        }
        return null;
      }
    });

    if (!result.isConfirmed) {
      return;
    }

    const nombrePerfil = String(result.value || '').trim();
    this.guardando = true;

    this.perfilesAccesoService.crearPerfil(this.token, nombrePerfil, BASELINE_PERMISSION_CODES).subscribe({
      next: (resp: any) => {
        this.guardando = false;

        if (resp?.profile?.id) {
          this.toastr.success('Perfil creado correctamente.', 'Atención', {
            timeOut: 3000,
            closeButton: true,
            progressBar: true
          });

          const nuevoPerfil: ProfileItem = {
            id: Number(resp.profile.id),
            name: String(resp.profile.name || nombrePerfil)
          };

          this.profiles = [...this.profiles, nuevoPerfil].sort((a, b) => a.name.localeCompare(b.name));
          this.irUltimaPaginaPerfiles();
          this.seleccionarPerfil(nuevoPerfil);
          return;
        }

        this.showError('No se pudo crear el perfil.');
      },
      error: () => {
        this.guardando = false;
        this.showError('No se pudo crear el perfil.');
      }
    });
  }

  async editarPerfil() {
    if (!this.canUpdateProfiles) {
      this.showError('No tienes permiso para editar perfiles.');
      return;
    }

    if (!this.selectedProfile) {
      this.showError('Selecciona un perfil primero.');
      return;
    }
    const selectedProfile = this.selectedProfile;

    const result = await Swal.fire({
      title: 'Editar perfil',
      input: 'text',
      inputValue: selectedProfile.name,
      inputPlaceholder: 'Nombre del perfil',
      showCancelButton: true,
      confirmButtonText: 'Guardar',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#6259ca',
      inputValidator: (value) => {
        if (!String(value || '').trim()) {
          return 'Debes ingresar un nombre de perfil';
        }
        return null;
      }
    });

    if (!result.isConfirmed) {
      return;
    }

    const nuevoNombre = String(result.value || '').trim();
    this.guardando = true;

    this.perfilesAccesoService.actualizarPerfil(this.token, selectedProfile.id, nuevoNombre).subscribe({
      next: () => {
        this.guardando = false;
        this.toastr.success('Perfil actualizado correctamente.', 'Atención', {
          timeOut: 3000,
          closeButton: true,
          progressBar: true
        });

        this.profiles = this.profiles
          .map((item) => item.id === selectedProfile.id ? { ...item, name: nuevoNombre } : item)
          .sort((a, b) => a.name.localeCompare(b.name));
        this.ajustarPaginaPerfiles();

        this.selectedProfile = {
          id: selectedProfile.id,
          name: nuevoNombre
        };
      },
      error: () => {
        this.guardando = false;
        this.showError('No se pudo actualizar el perfil.');
      }
    });
  }

  async eliminarPerfil() {
    if (!this.canDeleteProfiles) {
      this.showError('No tienes permiso para eliminar perfiles.');
      return;
    }

    if (!this.selectedProfile) {
      this.showError('Selecciona un perfil primero.');
      return;
    }

    const result = await Swal.fire({
      icon: 'warning',
      title: '¿Eliminar perfil?',
      text: `Se eliminará el perfil "${this.selectedProfile.name}".`,
      showCancelButton: true,
      confirmButtonColor: '#6259ca',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Si, eliminar',
      cancelButtonText: 'Cancelar'
    });

    if (!result.isConfirmed) {
      return;
    }

    const profileId = this.selectedProfile.id;
    this.guardando = true;

    this.perfilesAccesoService.eliminarPerfil(this.token, profileId).subscribe({
      next: () => {
        this.guardando = false;
        this.toastr.success('Perfil eliminado correctamente.', 'Atención', {
          timeOut: 3000,
          closeButton: true,
          progressBar: true
        });

        this.profiles = this.profiles.filter((item) => item.id !== profileId);
        this.ajustarPaginaPerfiles();
        this.selectedPermissionCodes = new Set<string>();

        if (this.profiles.length > 0) {
          this.seleccionarPerfil(this.profiles[0]);
        } else {
          this.selectedProfile = null;
        }
      },
      error: () => {
        this.guardando = false;
        this.showError('No se pudo eliminar el perfil.');
      }
    });
  }

  verUsuariosPerfil(profile: ProfileItem) {
    if (!this.canReadUsers) {
      this.showError('No tienes permiso para consultar usuarios.');
      return;
    }

    const abrirModal = (usuarios: UsuarioPerfilRow[]) => {
      const targetName = String(profile.name || '').trim().toLowerCase();
      const filtrados = usuarios
        .filter((u) => {
          const byId = u.profile_id != null && Number(u.profile_id) === Number(profile.id);
          const byName = String(u.perfil || '').trim().toLowerCase() === targetName;
          const activo = String(u.estado) === '1';
          return (byId || byName) && activo;
        })
        .sort((a, b) => String(a.nombre || '').localeCompare(String(b.nombre || '')));

      const total = filtrados.length;
      const rowsHtml = filtrados.length
        ? filtrados.map((u) => (
          `<tr>
            <td style="padding:6px 8px;border-bottom:1px solid #eef2ff;">${u.id}</td>
            <td style="padding:6px 8px;border-bottom:1px solid #eef2ff;">${this.escapeHtml(u.nombre)}</td>
            <td style="padding:6px 8px;border-bottom:1px solid #eef2ff;">${this.escapeHtml(u.usuario)}</td>
          </tr>`
        )).join('')
        : '<tr><td colspan="3" style="padding:10px;color:#6b7280;">No hay usuarios activos asignados a este perfil.</td></tr>';

      Swal.fire({
        title: `Usuarios del perfil: ${profile.name}`,
        html: `
          <div style="text-align:left;margin-bottom:8px;">
            <b>Total:</b> ${total}
          </div>
          <div style="max-height:340px;overflow:auto;border:1px solid #e5e7eb;border-radius:8px;">
            <table style="width:100%;border-collapse:collapse;font-size:13px;">
              <thead style="position:sticky;top:0;background:#f8fafc;">
                <tr>
                  <th style="text-align:left;padding:8px;border-bottom:1px solid #e5e7eb;">ID</th>
                  <th style="text-align:left;padding:8px;border-bottom:1px solid #e5e7eb;">Nombre</th>
                  <th style="text-align:left;padding:8px;border-bottom:1px solid #e5e7eb;">Usuario</th>
                </tr>
              </thead>
              <tbody>${rowsHtml}</tbody>
            </table>
          </div>
        `,
        width: 760,
        confirmButtonText: 'Cerrar',
        confirmButtonColor: '#6259ca'
      });
    };

    if (this.usuariosCache) {
      abrirModal(this.usuariosCache);
      return;
    }

    this.usuariosService.listarUsuarios(this.token).subscribe((resp: any) => {
      const usuarios = this.toArray(resp).map((item: any) => ({
        id: Number(item.id),
        nombre: String(item.nombre || ''),
        usuario: String(item.usuario || ''),
        perfil: String(item.perfil || ''),
        profile_id: item.profile_id != null ? Number(item.profile_id) : null,
        estado: String(item.estado ?? '0')
      })) as UsuarioPerfilRow[];

      this.usuariosCache = usuarios;
      abrirModal(usuarios);
    });
  }

  private toArray(resp: any): any[] {
    if (Array.isArray(resp)) {
      return resp;
    }

    if (Array.isArray(resp?.rows)) {
      return resp.rows;
    }

    if (Array.isArray(resp?.data)) {
      return resp.data;
    }

    return [];
  }

  private flattenPantallas(items: ScreenAccess[]): ScreenAccess[] {
    const flat: ScreenAccess[] = [];
    for (const item of items) {
      if (!item.children || !item.children.length) {
        flat.push(item);
      } else {
        flat.push(...this.flattenPantallas(item.children));
      }
    }
    return flat;
  }

  private matchesScreen(item: ScreenAccess, filtro: string): boolean {
    return item.label.toLowerCase().includes(filtro) || item.description.toLowerCase().includes(filtro);
  }

  toggleCollapse(key: string): void {
    if (this.collapsedSections.has(key)) {
      this.collapsedSections.delete(key);
    } else {
      this.collapsedSections.add(key);
    }
  }

  isCollapsed(key: string): boolean {
    return this.collapsedSections.has(key);
  }

  onBusquedaPerfilChange() {
    this.paginaPerfilesActual = 1;
  }

  cambiarPaginaPerfiles(delta: number) {
    const siguiente = this.paginaPerfilesActual + delta;
    this.paginaPerfilesActual = Math.min(Math.max(siguiente, 1), this.totalPaginasPerfiles);
  }

  private irUltimaPaginaPerfiles() {
    this.paginaPerfilesActual = this.totalPaginasPerfiles;
  }

  private ajustarPaginaPerfiles() {
    if (this.paginaPerfilesActual > this.totalPaginasPerfiles) {
      this.paginaPerfilesActual = this.totalPaginasPerfiles;
    }
    if (this.paginaPerfilesActual < 1) {
      this.paginaPerfilesActual = 1;
    }
  }

  private showError(message: string) {
    this.toastr.error(message, 'Atención', {
      timeOut: 3500,
      closeButton: true,
      progressBar: true
    });
  }

  private escapeHtml(value: string): string {
    return String(value || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }
}
